import { ConvertedDineInSession } from "../../../../../../services/converters/backendToUi/dineInConvertor";
import { ProductDetailsModal } from "../../../../../../interfaces/ProductObject";
import {
  orderedItemCount,
  selectPlacedRounds,
  totalBillAmount,
  totalPlateCount,
} from "../../../../../../services/helpers/DineInSummaryHelper";

// ─── Dine In board — derivation ───────────────────────────────────────────
// Turns "every live session at this business" into "every table on this floor,
// and what each one is doing". Pure: no React, no store, no fetching — so the
// tile logic can be reasoned about (and later tested) without a rendered tree.

/**
 * Fallback floor size, used for every business today because
 * BusinessConfigData.dineInTableCount has no save path yet (see its TODO).
 * Twelve is a plausible small-restaurant floor and, more importantly, is
 * harmless when wrong: extra tables render as free, and any table the API
 * reports beyond this count is still shown (see buildDineInBoard).
 */
export const DEFAULT_DINE_IN_TABLE_COUNT = 12;

/**
 * What a tile is doing right now.
 *
 * `billRequested` is deliberately present but NEVER produced: there is no
 * signal for it anywhere in the dining API today. It's typed and styled so the
 * board's visual language is complete and adding it later is a one-line change
 * in this file — not a promise that the feature works. Don't wire UI to it
 * expecting real data.
 */
export type DineInTableState = "free" | "active" | "needsAction" | "billRequested";

export interface DineInTableTile {
  /** Numeric table number — the identity used in the URL and for sorting. */
  tableNumber: number;
  /** Display form, zero-padded to two digits: "T01", "T12". */
  label: string;
  state: DineInTableState;
  /** Null for a free table. */
  session: ConvertedDineInSession | null;
  /** Plates on the table so far, rejected items excluded. */
  plateCount: number;
  /** Running ₹ total, rejected items and unresolved products excluded. */
  total: number;
  /** ISO timestamp the session opened, or null when free. */
  openedAt: string | null;
  /** Items still awaiting a kitchen decision — the "N to accept" badge. */
  pendingCount: number;
}

export interface DineInBoardStatsData {
  activeNow: number;
  needsAction: number;
  runningTotal: number;
}

/** "T01", "T12", "T130" — padded to two digits, wider only when it has to be. */
export function formatTableLabel(tableNumber: number): string {
  return `T${String(tableNumber).padStart(2, "0")}`;
}

/**
 * How long a table has been seated, in the compact form the tiles show:
 * "8m", "27m", "1h 05m". Takes `now` rather than reading the clock itself so
 * it stays pure and the caller controls how often the board re-renders.
 *
 * Returns "" for a missing or unparseable timestamp, and clamps negatives to
 * "0m" — a table whose createdAt is slightly in the future (clock skew between
 * the server and a counter tablet) should read as just-opened, not blank.
 */
export function formatElapsed(iso: string | null, now: number): string {
  if (!iso) return "";
  const started = new Date(iso).getTime();
  if (Number.isNaN(started)) return "";

  const minutes = Math.max(0, Math.floor((now - started) / 60000));
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/**
 * Builds one tile per table on the floor.
 *
 * The tile list is the UNION of 1..tableCount and every table number the API
 * actually reported. That union matters: tableCount is a guess (no save path
 * yet), so if a real session comes back for table 15 at a business configured
 * for 12, table 15 still appears. Showing a table that shouldn't exist is a
 * cosmetic oddity; hiding a table with live orders on it is a service failure.
 *
 * State is derived only from what the API can actually tell us:
 *  - no session          -> free      (the endpoint returns live tables only,
 *                                      so absence genuinely means empty)
 *  - any ORDERED item    -> needsAction (kitchen hasn't decided yet)
 *  - otherwise           -> active     (seated; everything accepted/served)
 */
export function buildDineInBoard(
  tableCount: number,
  sessions: ConvertedDineInSession[],
  products: ProductDetailsModal[]
): DineInTableTile[] {
  const byTable = new Map<number, ConvertedDineInSession>();
  for (const entry of sessions) {
    byTable.set(entry.session.tableNumber, entry);
  }

  const tableNumbers = new Set<number>(byTable.keys());
  for (let n = 1; n <= tableCount; n += 1) tableNumbers.add(n);

  return Array.from(tableNumbers)
    .sort((a, b) => a - b)
    .map((tableNumber) => {
      const entry = byTable.get(tableNumber) ?? null;

      if (!entry) {
        return {
          tableNumber,
          label: formatTableLabel(tableNumber),
          state: "free" as DineInTableState,
          session: null,
          plateCount: 0,
          total: 0,
          openedAt: null,
          pendingCount: 0,
        };
      }

      // Counts and money come from PLACED rounds only, never the raw session.
      // The endpoint returns draft rounds too, and a tile that included them
      // would bill a table for food it hasn't ordered — and disagree with the
      // ticket for that same table, which narrows the same way. See
      // selectPlacedRounds.
      const { items: placedItems } = selectPlacedRounds(entry.rounds, entry.roundItems);
      const pendingCount = orderedItemCount(placedItems);

      return {
        tableNumber,
        label: formatTableLabel(tableNumber),
        state: (pendingCount > 0 ? "needsAction" : "active") as DineInTableState,
        session: entry,
        plateCount: totalPlateCount(placedItems),
        total: totalBillAmount(placedItems, products),
        openedAt: entry.session.createdAt || null,
        pendingCount,
      };
    });
}

/**
 * The three numbers above the grid. Derived from the tiles rather than the raw
 * sessions so the strip can never disagree with what's rendered underneath it.
 */
export function buildDineInBoardStats(tiles: DineInTableTile[]): DineInBoardStatsData {
  let activeNow = 0;
  let needsAction = 0;
  let runningTotal = 0;

  for (const tile of tiles) {
    if (!tile.session) continue;
    activeNow += 1;
    if (tile.state === "needsAction") needsAction += 1;
    runningTotal += tile.total;
  }

  return { activeNow, needsAction, runningTotal };
}
